import { useTheme } from '@material-ui/core';
import {
  DataGrid,
  GridCallbackDetails,
  GridColumns,
  GridFeatureMode,
  GridRowIdGetter,
  GridRowParams,
  GridRowsProp,
  GridSelectionModel,
} from '@mui/x-data-grid';
import { translate } from '@portal/services/i18n';
import React from 'react';

interface IProps {
  columns: GridColumns;
  rows: GridRowsProp;
  page?: number;
  pageSize: number;
  rowCount?: number;
  orderBy: string;
  sort: 'asc' | 'desc';
  rowsPerPageOptions?: number[];
  getRowId?: GridRowIdGetter;
  onChange?: (params: advancedFilterModels.baseFilter) => void;
  paginationMode?: GridFeatureMode;
  lineText?: string;
  alignLeft?: boolean;
  checkboxSelection?: boolean;
  onSelectionModelChange?: (
    selection: GridSelectionModel,
    details: GridCallbackDetails
  ) => void;
  hideFooter?: boolean;
  onRowClick?: (params: GridRowParams) => void;
}

const DataTable: React.FC<IProps> = ({
  columns,
  rows,
  page,
  pageSize,
  rowCount,
  orderBy,
  sort,
  rowsPerPageOptions,
  getRowId,
  onChange,
  paginationMode = 'server',
  lineText,
  checkboxSelection,
  alignLeft,
  onSelectionModelChange,
  hideFooter,
  onRowClick,
}: IProps) => {
  const onPage = (page: number) => {
    if (pageSize === 100) {
      return;
    }

    const baseFilter: advancedFilterModels.baseFilter = {
      orderBy: orderBy,
      page,
      pageSize: pageSize,
      sort: sort,
      limit: pageSize,
    };

    if (onChange) {
      onChange(baseFilter);
    }
  };

  const onPageSizeChange = (newPageSize: number) => {
    const baseFilter: advancedFilterModels.baseFilter = {
      orderBy: orderBy,
      page: 0,
      pageSize: newPageSize,
      sort: sort,
      limit: newPageSize,
    };

    if (onChange) {
      onChange(baseFilter);
    }
  };

  const { palette } = useTheme();

  const CustomNoRowsOverlay = () => {
    return (
      <div className="datatable__no-rows-overlay">
        {lineText ?? translate('COMPONENTS.DATA_TABLE.NO_RESULT')}
      </div>
    );
  };

  return (
    <div className="datatable">
      <DataGrid
        autoHeight
        localeText={{
          MuiTablePagination: {
            labelRowsPerPage: translate(
              'COMPONENTS.DATA_TABLE.FOOTER.ROWS_PER_PAGE'
            ),
          },
        }}
        sx={{
          border: 0,
          '& .MuiDataGrid-columnSeparator': {
            visibility: 'hidden',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: `1px solid ${palette.grey[300]}`,
            backgroundColor: palette.grey[50],
            fontWeight: 600,
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${palette.grey[300]}`,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: palette.grey[50],
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${palette.grey[300]}`,
            backgroundColor: palette.background.paper,
            padding: '12px 16px',
            minHeight: '56px',
          },
          '& .MuiTablePagination-root': {
            color: palette.text.primary,
          },
          '& .MuiTablePagination-selectLabel': {
            margin: 0,
            fontSize: '0.875rem',
          },
          '& .MuiTablePagination-displayedRows': {
            margin: 0,
            fontSize: '0.875rem',
          },
          '& .MuiTablePagination-select': {
            paddingTop: '8px',
            paddingBottom: '8px',
          },
          '& .MuiTablePagination-actions': {
            marginLeft: '16px',
          },
        }}
        getRowId={getRowId}
        onSelectionModelChange={onSelectionModelChange}
        paginationMode={paginationMode}
        rows={rows}
        columns={columns.map((column) => ({
          ...column,
          sortable: false,
          filterable: false,
          align: alignLeft ? 'left' : 'center',
          headerAlign: alignLeft ? 'left' : 'center',
          headerClassName: 'datatable__header',
          description: column.headerName,
          cellClassName: 'datatable__cell',
        }))}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        onPageChange={(value) => onPage(value)}
        onPageSizeChange={(value) => onPageSizeChange(value)}
        components={{
          NoResultsOverlay: CustomNoRowsOverlay,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        disableColumnFilter
        disableColumnSelector
        disableSelectionOnClick
        disableColumnMenu
        rowsPerPageOptions={rowsPerPageOptions ?? [10, 25, 50]}
        pagination
        checkboxSelection={checkboxSelection}
        hideFooterSelectedRowCount
        hideFooter={hideFooter}
        onRowClick={onRowClick}
      />
    </div>
  );
};

export default DataTable;
